'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { useState, useLayoutEffect, useRef } from 'react';

interface NavigationItem {
  href: string;
  label: string;
  icon: React.ElementType;
  active: boolean;
}

export default function DashboardNav({
  navigationItems,
  pathname,
}: {
  navigationItems: NavigationItem[];
  pathname: string;
}) {
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const navRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navContainerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const activeIdx = navigationItems.findIndex(item => item.active);
    if (activeIdx !== -1 && navRefs.current[activeIdx] && navContainerRef.current) {
      const el = navRefs.current[activeIdx];
      const parent = navContainerRef.current;
      if (el && parent) {
        const elRect = el.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();
        setIndicatorStyle({
          left: elRect.left - parentRect.left,
          width: elRect.width,
        });
      }
    }
  }, [pathname, navigationItems]);

  return (
    <div
      ref={navContainerRef}
      className="hidden lg:flex items-center gap-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-1 relative"
      style={{ overflow: 'hidden' }}
    >
      {/* Sliding indicator */}
      <div
        className="absolute top-1 left-0 h-[calc(100%-0.5rem)] bg-gradient-to-r from-purple-500 to-pink-500 rounded-md transition-all duration-500 z-0 bg-opacity-100"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
          pointerEvents: 'none',
        }}
      ></div>
      {navigationItems.map((item, idx) => {
        const IconComponent = item.icon;
        return (
          <div
            key={item.href}
            className="relative"
            ref={el => {
              navRefs.current[idx] = el;
            }}
          >
            <Link
              href={item.href}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-300',
                'relative z-10',
                item.active
                  ? 'text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              )}
              style={{ overflow: 'hidden' }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <IconComponent className="w-4 h-4" />
                <span>{item.label}</span>
              </span>
            </Link>
          </div>
        );
      })}
    </div>
  );
}
